import { ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getActiveChipName,
  getPotentialChips,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { IElementTypesBySquadPosition } from "core-integration/src/store/element-types/types";
import {
  canAutocomplete,
  canReset,
  getErrors,
  getFreeTransfersRemaining,
  getProposedElements,
  getToSpend,
  getTransferCosts,
} from "core-integration/src/store/squad/reducers";
import { autoComplete, reset } from "core-integration/src/store/squad/thunks";
import { IProposedElements } from "core-integration/src/store/squad/types";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import RichUncle from "../../img/icons/rich-uncle.svg?react";
import Wildcard from "../../img/icons/wildcard.svg?react";
import { ChipName, getChipName } from "../../utils/chips";
import Alert from "../Alert";
import Button from "../Button";
import Dialog from "../Dialog";
import DialogManager from "../DialogManager";
import { ScoreboardPanel } from "./ScoreboardPanel";

const StyledBasic = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]};
  border: 1px solid ${({ theme }) => theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: flex;
    border-radius: ${({ theme }) => theme.radii[1]};
  }
`;

const ScoreboardButtons = styled(Flex)`
  display: flex;
  gap: ${({ theme }) => theme.space[3]};
  flex: 1;
  align-items: center;
  padding: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    border-right: 0;
  }
`;

const ScoreboardPanels = styled(Flex)`
  flex: 1;
  border-top: 1px solid ${({ theme }) => theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    border-top: 0;
    border-left: 1px solid ${({ theme }) => theme.colors.white};
  }
`;

const PanelWrapper = styled(Box)`
  flex: 1;
  border-left: 1px solid ${({ theme }) => theme.colors.white};

  &:first-of-type {
    border-left: 0;
  }
`;

const ScoreboardUnitWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space[3]};
  flex-basis: 900px;
  margin: 0 ${({ theme }) => theme.space[2]};
`;

const ScoreboardUnitButtonWrap = styled.div`
  display: flex;
  flex: 1 0 350px;
  gap: ${({ theme }) => theme.space[2]};
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex: 1 0 370px;
  }
`;

const ChipItem = styled.li`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]};
  padding: ${({ theme }) => theme.space[2]};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radii[0]};
  background-color: ${({ theme }) => theme.colors.bgWhiteOpacity50};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-direction: row;
  }
`;

const ChipIcon = styled.div`
  svg {
    width: 50px;
    height: 50px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    svg {
      width: 60px;
      height: 60px;
    }
  }
`;

const TransfersDatatWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${({ theme }) => theme.space[2]} ${({ theme }) => theme.space[1]};
`;

const CostScoreboardUnit = styled.div`
  flex: 1 0 50%;
  padding: 0 ${({ theme }) => theme.space[2]};

  &:nth-child(4) > div {
    border-bottom-width: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1;

    &:nth-child(3) > div {
      border-bottom-width: 0;
    }

    &:nth-child(4) > div {
      border-bottom-width: 1px;
    }
  }
`;

const DialogHeading = styled.div`
  padding: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.blueDarker2};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
`;

interface IBasicOwnProps {
  elementTypesByPosition: IElementTypesBySquadPosition;
  isBudgetExceeded: boolean;
  isNeedElements: boolean;
  proposedElements: IProposedElements;
}

type IBasicProps = IBasicOwnProps & ISharedScoreboardProps;

const Basic: React.FC<IBasicProps> = ({
  isBudgetExceeded,
  isNeedElements,
  proposedElements,
  elementTypesByPosition,
  currencyDivisor,
  canResetTeam,
  canAutoPick,
  toSpend,
  autoPick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();

  return (
    <StyledBasic>
      <ScoreboardButtons>
        <Button disabled={!canAutoPick} onClick={autoPick} width={1}>
          {t("squad.autoPick", "Auto Pick")}
        </Button>

        <Button
          disabled={!canResetTeam}
          onClick={() => dispatch(reset())}
          type="reset"
          width={1}
        >
          {t("reset", "Reset")}
        </Button>
      </ScoreboardButtons>
      <ScoreboardPanels>
        <PanelWrapper>
          <ScoreboardPanel
            heading={t("squad.playersSelected", "Players Selected")}
            value={`${Object.keys(proposedElements).length} / ${
              Object.keys(elementTypesByPosition).length
            }`}
            isError={isNeedElements}
            isSuccess={!isNeedElements}
          />
        </PanelWrapper>
        <PanelWrapper>
          <ScoreboardPanel
            heading={t("squad.moneyRemaining", "Money Remaining")}
            value={`${integerToMoney(toSpend, currencyDivisor)}`}
            isError={isBudgetExceeded}
            isSuccess={!isBudgetExceeded}
          />
        </PanelWrapper>
      </ScoreboardPanels>
    </StyledBasic>
  );
};

interface IExplainChipDialogProps {
  chip: string;
  handleHide: () => void;
  transferCosts: number;
}

const ExplainChipDialog: React.FC<IExplainChipDialogProps> = ({
  chip,
  handleHide,
  transferCosts,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {chip === "wildcard" && t("squad.playWildcardTest", "Play Wildcard")}
          {chip === "rich" && t("squad.playRichUncleText", "Play Rich Uncle")}
        </DialogHeading>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Alert type="info">
          {transferCosts ? (
            <>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText1",
                    "You are now proposing to play your wildcard. Unlimited transfers can be made this Round without points deduction.",
                  )}
                {chip === "rich" &&
                  t(
                    "squad.richUncleText1",
                    "You are now proposing to play your Rich Uncle chip. Unlimited transfers with an unlimted budget can be made this Round without points deduction.",
                  )}
              </p>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText2",
                    "Your wildcard will not be activated until you confirm your transfers.",
                  )}
                {chip === "rich" &&
                  t(
                    "squad.richUncleText2",
                    "Your Rich Uncle will not be activated until you confirm your transfers",
                  )}
              </p>
            </>
          ) : (
            <p>
              {chip === "wildcard" &&
                t(
                  "squad.wildcardText3",
                  "To activate your wildcard you must be making enough proposed transfers that points will be deducted.",
                )}
              {chip === "rich" &&
                t(
                  "squad.richUncleText3",
                  "To activate your Rich Uncle chip you must be making enough proposed transfers that points will be deducted.",
                )}
            </p>
          )}
        </Alert>
      </Dialog.Body>
    </Dialog>
  );
};

interface ISharedScoreboardProps {
  currencyDivisor: number;
  canResetTeam: boolean;
  canAutoPick: boolean;
  toSpend: number;
  autoPick: () => void;
}
interface ICostOwnProps {
  isBudgetExceeded: boolean;
}

type ICostProps = ICostOwnProps & ISharedScoreboardProps;

const Cost: React.FC<ICostProps> = ({
  isBudgetExceeded,
  currencyDivisor,
  canAutoPick,
  canResetTeam,
  toSpend,
  autoPick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const activeChipName = useSelector(getActiveChipName);
  const freeTransfersRemaining = useSelector(getFreeTransfersRemaining);
  const proposedChipName = useSelector(getProposedChipName);
  const transferCosts = useSelector(getTransferCosts);
  const chips = useSelector(getPotentialChips);

  const cancelChip = (chip: string) => dispatch(cancelProposedChip(chip));
  const proposeChip = (chip: string) => dispatch(proposeAvailableChip(chip));

  const richUncleChip = chips.filter((c) => c.name === "rich")[0];
  const wildCardChip = chips.filter((c) => c.name === "wildcard")[0];

  const getChipIcon = (chipName: string) => {
    switch (chipName) {
      case "rich":
        return <RichUncle />;
      case "wildcard":
        return <Wildcard />;
      default:
        return null;
    }
  };

  return (
    <>
      <ScoreboardUnitWrap>
        <ScoreboardUnitButtonWrap>
          <Button disabled={!canAutoPick} onClick={autoPick} width={1}>
            {t("squad.autoPick", "Auto Pick")}
          </Button>

          <Button
            disabled={!canResetTeam}
            onClick={() => dispatch(reset())}
            type="reset"
            width={1}
          >
            {t("squad.reset", "Reset")}
          </Button>
        </ScoreboardUnitButtonWrap>
        <ScoreboardUnitButtonWrap>
          {activeChipName ? (
            <CostScoreboardUnit>
              <ScoreboardPanel
                heading={t("scoreboard.chip.active", "Active Chip")}
                value={getChipName(activeChipName as ChipName, t)}
              />
            </CostScoreboardUnit>
          ) : wildCardChip.status_for_entry === "played" ? (
            <ScoreboardPanel heading="Wildcard" value={"Played"} />
          ) : wildCardChip.status_for_entry === "active" ? (
            <CostScoreboardUnit>
              <ScoreboardPanel heading="Wildcard" value={"Active"} />
            </CostScoreboardUnit>
          ) : proposedChipName === "wildcard" ? (
            <ChipItem>
              <ChipIcon>{getChipIcon("wildcard")}</ChipIcon>
              <Button width={1} onClick={() => cancelChip("wildcard")}>
                {t("squad.cancelWildcardText", "Cancel Wildcard")}
              </Button>
            </ChipItem>
          ) : (
            <DialogManager
              render={(showDialog, handleShow, handleHide) => (
                <>
                  <ChipItem>
                    <ChipIcon>{getChipIcon("wildcard")}</ChipIcon>
                    <Button
                      disabled={
                        proposedChipName === "rich" || activeChipName === "rich"
                      }
                      width={1}
                      onClick={handleShow}
                    >
                      {t("squad.playWildcardTest", "Play Wildcard")}
                    </Button>
                  </ChipItem>
                  {showDialog && (
                    <ExplainChipDialog
                      chip="wildcard"
                      handleHide={() => {
                        handleHide();
                        if (transferCosts) {
                          proposeChip("wildcard");
                        }
                      }}
                      transferCosts={transferCosts}
                    />
                  )}
                </>
              )}
            />
          )}

          {activeChipName ? (
            <>&nbsp;</>
          ) : richUncleChip.status_for_entry === "played" ? (
            <ScoreboardPanel heading="Rich Uncle" value={"Played"} />
          ) : richUncleChip.status_for_entry === "active" ? (
            <CostScoreboardUnit>
              <ScoreboardPanel heading="Rich Uncle" value={"Active"} />
            </CostScoreboardUnit>
          ) : proposedChipName === "rich" ? (
            <ChipItem>
              <ChipIcon>{getChipIcon("rich")}</ChipIcon>
              <Button width={1} onClick={() => cancelChip("rich")}>
                {t("squad.cancelRichUncleText", "Cancel Rich Uncle")}
              </Button>
            </ChipItem>
          ) : (
            <DialogManager
              render={(showDialog, handleShow, handleHide) => (
                <>
                  <ChipItem>
                    <ChipIcon>{getChipIcon("rich")}</ChipIcon>
                    <Button
                      disabled={
                        proposedChipName === "wildcard" ||
                        activeChipName === "wildcard"
                      }
                      width={1}
                      onClick={handleShow}
                    >
                      {t("squad.playRichUncleText", "Play Rich Uncle")}
                    </Button>
                  </ChipItem>
                  {showDialog && (
                    <ExplainChipDialog
                      chip="rich"
                      handleHide={() => {
                        handleHide();
                        if (transferCosts) {
                          proposeChip("rich");
                        }
                      }}
                      transferCosts={transferCosts}
                    />
                  )}
                </>
              )}
            />
          )}
        </ScoreboardUnitButtonWrap>
      </ScoreboardUnitWrap>
      <TransfersDatatWrap>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.freeTransfers", "Free Transfers")}
            value={
              activeChipName === "wildcard" || activeChipName === "rich"
                ? "Unlimited"
                : `${freeTransfersRemaining}`
            }
          />
        </CostScoreboardUnit>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.cost", "Cost")}
            value={`${transferCosts} pts`}
            isError={transferCosts > 0}
          />
        </CostScoreboardUnit>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.moneyRemaining", "Money Remaining")}
            value={
              proposedChipName === "rich" || activeChipName === "rich"
                ? `-- mNOK`
                : `${integerToMoney(toSpend, currencyDivisor)}`
            }
            isError={isBudgetExceeded}
            isSuccess={!isBudgetExceeded}
          />
        </CostScoreboardUnit>
      </TransfersDatatWrap>
    </>
  );
};

interface IProps {
  scoreboard: string;
}

const Scoreboard = ({ scoreboard }: IProps) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const proposedElements = useSelector(getProposedElements);
  const elementTypesByPosition = useSelector(getElementTypesBySquadPosition);
  const errors = useSelector(getErrors);
  const isNeedElements = Boolean(errors.needElements);
  const isBudgetExceeded = Boolean(errors.budgetExceeded);
  const currencyDivisor = 10;
  const canResetTeam = useSelector(canReset);
  const canAutoPick = useSelector(canAutocomplete);
  const toSpend = useSelector(getToSpend);

  const autoPick = () => {
    if (!dispatch(autoComplete())) {
      // We should do something :-)
      window.console.log("Failed to autocomplete");
    }
  };

  if (!elementTypesByPosition) {
    return null;
  }
  switch (scoreboard) {
    case "basic":
      return (
        <Basic
          elementTypesByPosition={elementTypesByPosition}
          isNeedElements={isNeedElements}
          isBudgetExceeded={isBudgetExceeded}
          proposedElements={proposedElements}
          currencyDivisor={currencyDivisor}
          canResetTeam={canResetTeam}
          canAutoPick={canAutoPick}
          toSpend={toSpend}
          autoPick={autoPick}
        />
      );
    case "cost":
      return (
        <Cost
          isBudgetExceeded={isBudgetExceeded}
          currencyDivisor={currencyDivisor}
          canResetTeam={canResetTeam}
          canAutoPick={canAutoPick}
          toSpend={toSpend}
          autoPick={autoPick}
        />
      );
    default:
      return null;
  }
};

export default Scoreboard;
